/**
 * 인포트렌드 사이징 툴 — 계산 (v2.233.0)
 *
 * 엑셀 「Sizing Tool_v2.1.xlsx」의 수식을 그대로 옮긴 순수 함수. 화면(components/sizing/*)과 그물(scripts/check-sizing.ts)이
 * 같은 함수를 부른다. 그물은 엑셀이 셀에 남긴 계산 결과(기본 입력값 기준)와 이 함수의 답을 맞춰 본다.
 *
 * 엑셀과 일부러 다르게 둔 곳(각 함수 주석에 ⚠ 로 적음):
 *   · 0 으로 나누는 자리(처리량 0 · 「없음」 프레임)는 #DIV/0! 대신 0 으로.
 *   · VDI 모델 판정의 한 가지(47,000~75,000 IOPS · 361~444개)는 엑셀 수식에 빈 인자가 끼어 늘 빈 글자가 나온다 —
 *     옆 가지들과 같은 뜻(「4000U」)으로 채웠다.
 *   · CCTV 컨트롤러 판정의 경계값 352 는 엑셀에서 어느 쪽도 아니어서(「-」) 단일 컨트롤러 쪽으로 넣었다.
 */
import {
  DISK_PERF_TABLE, DISK_RESERVED_MB, DRIVE_SIZE_MB, JBOD_FORM_FACTORS, LD_META_MB, ME_BITRATE, ME_CONFIGS, ME_HOST_BOARDS,
  ME_MEM_UPGRADE, ME_PERF, MSRP, RAID_FORM_FACTORS, SV_BITRATE, SV_CONFIGS, SV_CONTROLLER_LIMIT, SV_HOST_BOARDS, SV_MAX_FPS,
  VDI_BOOT_SSD, VDI_IOPS_PER_DISK, VDI_MODELS, VDI_WORKERS, VIRT_RATIO, driveTbToRealMb, usableTbPerDrive,
} from './data';
import type { MeFamily, MeJbod, MeSystem } from './data';
import type {
  BlockSize, DiskPerfRow, DriveType, IoType, JbodFormFactor, MeCodec, MeFrameRate, MeFrameSize, MeRaid, MeSeries, PerfUnit,
  RaidFormFactor, RaidLevel, RandomSeq, SvHostBoard, SvQuality, SvResolution, SvStream, VdiClone, VdiDriveType, VdiRaid,
} from './types';

/** 엑셀 ROUNDUP(x, 0) — 양수를 올림. 부동소수 찌꺼기(2.0000000004)로 한 개가 더 붙지 않게 아주 작은 값을 뺀다. */
export function roundUp(x: number): number {
  if (!Number.isFinite(x)) return x;
  return Math.ceil(x - 1e-9);
}

/* ═══════════════════════ 1. 용량 ═══════════════════════ */

export interface DrivesNeededInput {
  /** 필요한 사용 가능 용량(TB) */
  usableTb: number;
  /** 디스크 1개 표기 용량(TB) */
  driveTb: number;
  /** 스페어 디스크 수 */
  spare: number;
  /** 논리 드라이브(LD) 수 */
  logicalDrives: number;
}
export interface DrivesNeededResult {
  raid: 'RAID1' | 'RAID5' | 'RAID6';
  /** 필요한 디스크 수 */
  drives: number;
  /** 베이 수별 필요한 인클로저 수 */
  enclosures: Record<12 | 16 | 24 | 48 | 60, number>;
}

/**
 * 「필요한 디스크 수」 — 엑셀 Capacity 시트 윗부분.
 *   기본 = 필요 MB ÷ (디스크 실제 MB − 256) ÷ (1 − 0.002)
 *   RAID 6 = 올림(기본 + LD × 2 + 스페어) · RAID 5 = 올림(기본 + LD × 1 + 스페어) · RAID 1 = 올림(기본 × 2 + 스페어)
 */
export function calcDrivesNeeded(i: DrivesNeededInput): DrivesNeededResult[] {
  const usableMb = i.usableTb * 1024 * 1024;
  const realMb = driveTbToRealMb(i.driveTb);
  const base = usableMb / (realMb - DISK_RESERVED_MB) / (1 - VIRT_RATIO);
  const enc = (n: number): Record<12 | 16 | 24 | 48 | 60, number> => ({ 12: roundUp(n / 12), 16: roundUp(n / 16), 24: roundUp(n / 24), 48: roundUp(n / 48), 60: roundUp(n / 60) });
  const r1 = roundUp(base * 2 + i.spare);
  const r5 = roundUp(base + i.logicalDrives * 1 + i.spare);
  const r6 = roundUp(base + i.logicalDrives * 2 + i.spare);
  return [
    { raid: 'RAID1', drives: r1, enclosures: enc(r1) },
    { raid: 'RAID5', drives: r5, enclosures: enc(r5) },
    { raid: 'RAID6', drives: r6, enclosures: enc(r6) },
  ];
}

export interface UsableCapacityInput {
  driveTb: number;
  totalDrives: number;
  spare: number;
  logicalDrives: number;
  /** 고급 — 비우면 엑셀 기본값(256 · 256 · 0.002) */
  reservedMb?: number;
  metaMb?: number;
  virtRatio?: number;
}
export interface UsableCapacityResult { raid: 'RAID1' | 'RAID5' | 'RAID6'; usableMb: number; usableTb: number }

/**
 * 「총 사용 가능 용량」 — 엑셀 Capacity 시트 아랫부분.
 *   RAID 1 = ((실제 MB − 예약) × ((N − 스페어) ÷ LD ÷ 2) − 메타) × (1 − 가상화) × LD
 *   RAID 5 = ((실제 MB − 예약) × ((N − 스페어) ÷ LD − 1) − 메타) × (1 − 가상화) × LD
 *   RAID 6 = … − 2 …
 */
export function calcUsableCapacity(i: UsableCapacityInput): UsableCapacityResult[] {
  const reserved = i.reservedMb ?? DISK_RESERVED_MB;
  const meta = i.metaMb ?? LD_META_MB;
  const virt = i.virtRatio ?? VIRT_RATIO;
  const realMb = driveTbToRealMb(i.driveTb);
  const ld = i.logicalDrives;
  const perLd = (i.totalDrives - i.spare) / ld;
  const f = (dataPerLd: number): number => ((realMb - reserved) * dataPerLd - meta) * (1 - virt) * ld;
  const mk = (raid: 'RAID1' | 'RAID5' | 'RAID6', mb: number): UsableCapacityResult => ({ raid, usableMb: mb, usableTb: mb / 1024 / 1024 });
  return [mk('RAID1', f(perLd / 2)), mk('RAID5', f(perLd - 1)), mk('RAID6', f(perLd - 2))];
}

/* ═══════════════════════ 2. 성능 (IOMeter) ═══════════════════════ */

export interface PerfSelection { rs: RandomSeq; io: IoType; raid: RaidLevel; drive: DriveType; block: BlockSize }

/** 성능 표에서 한 줄 찾기 — 다섯 조건이 모두 맞는 줄(엑셀 MATCH(1, (…)*(…))) */
export function findPerfRow(sel: PerfSelection): DiskPerfRow | undefined {
  return DISK_PERF_TABLE.find((r) => r.rs === sel.rs && r.io === sel.io && r.raid === sel.raid && r.drive === sel.drive && r.block === sel.block);
}

/** 앞 단계에서 고른 값에 맞는 다음 단계 선택지 — 표에 있는 조합만 보여 주려고 표에서 뽑는다 */
export function perfIoOptions(rs: RandomSeq): IoType[] {
  return uniq(DISK_PERF_TABLE.filter((r) => r.rs === rs).map((r) => r.io));
}
export function perfRaidOptions(rs: RandomSeq, io: IoType): RaidLevel[] {
  return uniq(DISK_PERF_TABLE.filter((r) => r.rs === rs && r.io === io).map((r) => r.raid));
}
export function perfDriveOptions(rs: RandomSeq, io: IoType, raid: RaidLevel): DriveType[] {
  return uniq(DISK_PERF_TABLE.filter((r) => r.rs === rs && r.io === io && r.raid === raid).map((r) => r.drive));
}
export function perfBlockOptions(sel: Omit<PerfSelection, 'block'>): BlockSize[] {
  return uniq(DISK_PERF_TABLE.filter((r) => r.rs === sel.rs && r.io === sel.io && r.raid === sel.raid && r.drive === sel.drive).map((r) => r.block));
}
function uniq<T>(xs: T[]): T[] {
  return [...new Set(xs)];
}

export interface IometerDrivesInput extends PerfSelection {
  unit: PerfUnit;
  /** 목표 성능 (IOPS 또는 MB/s) */
  target: number;
  raidFF: RaidFormFactor;
  jbodFF: JbodFormFactor;
}
export interface IometerDrivesResult {
  /** 디스크 1개 성능 (unit 단위) */
  perDisk: number;
  /** 목표를 내는 데 정확히 필요한 디스크 수 */
  exact: number;
  /** RAID 인클로저에 넣는 권장 디스크 수 (= 그 폼팩터의 베이 수 · 논리 드라이브 만들기 좋게 가득 채운다) */
  raidRecommended: number;
  raidBay: number;
  jbodBay: number;
  /** JBOD 에 정확히 필요한 디스크 수 (RAID 인클로저로 모자라는 만큼) */
  jbodExact: number;
  /** JBOD 권장 디스크 수 (단위의 배수로 올림) · null = 이 JBOD 로는 안 됨 */
  jbodRecommended: number | null;
  /** 권장 총 디스크 수 · null = 안 됨 */
  total: number | null;
  /** 안 될 때 안내 */
  message: string | null;
}

/** 「목표 성능에 필요한 디스크 수」 — 엑셀 Performance-IOMeter 윗부분 */
export function calcIometerDrives(i: IometerDrivesInput): IometerDrivesResult | null {
  const row = findPerfRow(i);
  if (!row) return null;
  const perDisk = i.unit === 'IOPS' ? row.iops : row.mbps;
  const raidBay = RAID_FORM_FACTORS.find((f) => f.key === i.raidFF)?.bay ?? 0;
  const jb = JBOD_FORM_FACTORS.find((f) => f.key === i.jbodFF) ?? JBOD_FORM_FACTORS[JBOD_FORM_FACTORS.length - 1]!;
  const exact = perDisk > 0 ? roundUp(i.target / perDisk) : 0;
  const raidRecommended = raidBay;
  const jbodExact = exact > raidRecommended ? exact - raidRecommended : 0;
  let jbodRecommended: number | null = 0;
  let message: string | null = null;
  if (jbodExact > 0) {
    if (jb.unit === 0) {
      jbodRecommended = null;
      message = 'RAID 인클로저 베이 수보다 디스크가 더 필요합니다 — JBOD 폼팩터를 고르세요.';
    } else if (jbodExact > jb.maxDrives) {
      jbodRecommended = null;
      message = `이 JBOD(${jb.label})로 이을 수 있는 최대(${jb.maxDrives}개)를 넘습니다 — 고밀도 JBOD(4U 48베이 · 4U 60베이)를 골라 보세요.`;
    } else {
      jbodRecommended = roundUp(jbodExact / jb.unit) * jb.unit;
    }
  }
  const total = jbodRecommended === null ? null : raidRecommended + jbodRecommended;
  return { perDisk, exact, raidRecommended, raidBay, jbodBay: jb.bay, jbodExact, jbodRecommended, total, message };
}

export interface IometerPerfInput extends PerfSelection { unit: PerfUnit; drives: number }

/** 「디스크 수로 낼 수 있는 성능」 — 엑셀 Performance-IOMeter 아랫부분. 총 성능 = 디스크 1개 성능 × 디스크 수 */
export function calcIometerPerf(i: IometerPerfInput): { perDisk: number; total: number } | null {
  const row = findPerfRow(i);
  if (!row) return null;
  const perDisk = i.unit === 'IOPS' ? row.iops : row.mbps;
  return { perDisk, total: perDisk * i.drives };
}

/* ═══════════════════════ 3. 영상 제작 (M&E) ═══════════════════════ */

export interface MeVideo { frame: MeFrameSize; fps: MeFrameRate; codec: MeCodec; streams: number }
export interface MeInput {
  read: MeVideo;
  write: MeVideo;
  usableTb: number;
  driveTb: number;
  /** 디스크 1개 가격(USD) — 정가 합계에 더한다 · 모르면 0 */
  drivePrice: number;
  raid: MeRaid;
  /** 인클로저(서랍)마다 스페어 수 · 0 또는 1 */
  spare: number;
  series: MeSeries;
}
export interface MeRow {
  system: string;
  jbod: string;
  family: MeFamily;
  /** 「1R+2J」 · null = 이 구성으로는 안 됨 */
  config: string | null;
  totalDrives: number | null;
  usableTb: number | null;
  spares: number | null;
  heightU: number | null;
  /** 정가 합계(USD) · null = 안 됨 또는 미정 */
  msrp: number | null;
  msrpNote: string | null;
  msrpPerMbps: number | null;
  msrpPerTb: number | null;
}
export interface MeResult {
  readMbps: number;
  writeMbps: number;
  /** 제품군별 필요한 디스크 수 — null = 그 제품군의 최대 처리량을 넘어 안 됨 */
  needed: Record<MeFamily, { fromRead: number | null; fromWrite: number | null; fromCapacity: number; total: number | null }>;
  rows: MeRow[];
}

/** 스트림 처리량(MB/s) = 프레임 크기·압축·fps 표 값 × 스트림 수 */
export function meThroughput(v: MeVideo): number {
  return ME_BITRATE[v.frame][v.codec][v.fps] * v.streams;
}

function meDrivesFor(family: MeFamily, mbps: number, frame: MeFrameSize, kind: 'read' | 'write'): number | null {
  const idx = (family === 'DS3000' ? 0 : 1) + (kind === 'read' ? 0 : 2);
  const perf = ME_PERF[frame];
  if (mbps <= 0) return 0;                       // ⚠ 엑셀은 0 ÷ 0 → #DIV/0!
  if (mbps > perf.max[idx]) return null;         // 그 제품군의 최대를 넘음 (엑셀 10,000,000,000)
  return roundUp(mbps / perf.perDrive[idx]);
}

/** 「영상 제작 사이징」 — 엑셀 M&E 시트 + M&E Database */
export function calcMediaEnt(i: MeInput): MeResult {
  const readMbps = meThroughput(i.read);
  const writeMbps = meThroughput(i.write);
  const tbPerDrive = usableTbPerDrive(i.driveTb);
  const fromCapacity = roundUp(i.usableTb / tbPerDrive);
  const needed = {} as MeResult['needed'];
  for (const fam of ['DS3000', 'DS4000'] as const) {
    const fromRead = meDrivesFor(fam, readMbps, i.read.frame, 'read');
    const fromWrite = meDrivesFor(fam, writeMbps, i.write.frame, 'write');
    const total = fromRead === null || fromWrite === null ? null : Math.max(fromRead, fromWrite, fromCapacity);
    needed[fam] = { fromRead, fromWrite, fromCapacity, total };
  }
  const parity = i.raid === 'RAID6' ? 2 : 1;
  const s = i.spare;
  const rows: MeRow[] = ME_CONFIGS.map(({ system, jbod }) => meRow(system, jbod, needed[system.family].total, parity, s, tbPerDrive, i, readMbps));
  return { readMbps, writeMbps, needed, rows };
}

function meRow(sys: MeSystem, jb: MeJbod, need: number | null, parity: number, s: number, tbPerDrive: number, i: MeInput, readMbps: number): MeRow {
  const empty: MeRow = { system: sys.name, jbod: jb.name, family: sys.family, config: null, totalDrives: null, usableTb: null, spares: null, heightU: null, msrp: null, msrpNote: null, msrpPerMbps: null, msrpPerTb: null };
  if (need === null) return empty;
  // 데이터 디스크 수 — 인클로저(또는 서랍)마다 패리티 디스크(RAID6 2 · RAID5 1)와 스페어를 뺀다
  const availSys = sys.drawers ? 3 * (sys.unit - parity - s) : sys.bay - parity - s;
  const availJbodUnit = jb.unit - parity - s;
  const jbodDrives = Math.max(need - availSys, 0);
  const g = roundUp(jbodDrives / availJbodUnit);            // JBOD 인클로저 수(체인) 또는 서랍 수(서랍식)
  if (g > jb.maxUnits) return empty;
  const h = jb.drawers ? roundUp(g / 3) : g;                 // JBOD 대수
  const unusedJbodDrawers = jb.drawers ? (3 - (g % 3)) % 3 : 0;
  let usable: number;
  let sysDrives: number;
  let unusedSysDrawers = 0;
  if (sys.drawers) {
    // 48·60베이 RAID 는 서랍 1·2·3개 중 필요한 만큼만 채운다 (엑셀 J14·L14·M14)
    const u = sys.unit - parity - s;
    const tiers = need <= u ? 1 : need <= 2 * u ? 2 : 3;
    usable = ((tiers === 3 ? availSys : u * tiers) + availJbodUnit * g) * tbPerDrive;
    sysDrives = sys.unit * tiers;
    unusedSysDrawers = 3 - tiers;
  } else {
    usable = (availSys + availJbodUnit * g) * tbPerDrive;
    sysDrives = sys.bay;
  }
  const totalDrives = sysDrives + jb.unit * g;
  const heightU = sys.heightU + jb.heightU * h;
  const spares = sys.drawers ? s * ((h + 1) * 3 - unusedSysDrawers - unusedJbodDrawers) : s * (1 + g);
  const usableRounded = Math.round(usable * 10) / 10;
  let msrp: number | null = null;
  let msrpNote: string | null = null;
  if (jb.msrp === null) {
    msrpNote = '당시 정가 미정';
  } else {
    const base = i.series === 'highIops' ? sys.msrpHighIops : sys.msrpStd;
    const mem = sys.family === 'DS4000' ? (i.series === 'highIops' ? ME_MEM_UPGRADE.ds4000highIops : ME_MEM_UPGRADE.ds4000std) : ME_MEM_UPGRADE.ds3000;
    const hostBoards = ME_HOST_BOARDS[sys.family] * MSRP['FC 16Gb Host board']!;
    msrp = Math.floor(base + mem + jb.msrp * h + i.drivePrice * totalDrives + hostBoards);
  }
  return {
    system: sys.name, jbod: jb.name, family: sys.family,
    config: `1R+${h}J`, totalDrives, usableTb: usableRounded, spares, heightU, msrp, msrpNote,
    msrpPerMbps: msrp === null || readMbps <= 0 ? null : msrp / readMbps,
    msrpPerTb: msrp === null || usableRounded <= 0 ? null : msrp / usableRounded,   // 엑셀도 소수 1자리로 둥근 용량으로 나눈다
  };
}

/* ═══════════════════════ 4. CCTV (감시) ═══════════════════════ */

export interface SvCamera {
  stream: SvStream;
  resolution: SvResolution;
  quality: SvQuality;
  fps: number;
  count: number;
  /** 하루 녹화 시간 */
  hoursPerDay: number;
  /** 보관 일수 */
  days: number;
}
export interface SvInput {
  cameras: SvCamera[];
  /** 인클로저(서랍)마다 스페어 수 · 0 또는 1 */
  spare: number;
  hostBoard: SvHostBoard;
  /** 디스크 크기(TB)별 1개 가격(USD) · 없으면 0 */
  drivePrices: Readonly<Record<number, number>>;
  /** 표에 보여 줄 디스크 크기(TB) */
  driveSizes: readonly number[];
}
export interface SvCameraResult {
  /** 카메라 1대 · 1fps 당 Mbps */
  perCameraPerFps: number;
  /** 이 종류 전체 Mbps */
  mbps: number;
  /** 이 종류 전체 MB/s */
  mbPerSec: number;
  /** 보관에 필요한 TB (10% 여유 포함) */
  storageTb: number;
  /** 엑셀 각주의 최대 FPS 를 넘었나 */
  overMaxFps: boolean;
}
export interface SvRow {
  driveTb: number;
  system: string;
  jbod: string;
  config: string | null;
  totalDrives: number | null;
  usableTb: number | null;
  spares: number | null;
  heightU: number | null;
  msrp: number | null;
  msrpPerTb: number | null;
}
export interface SvResult {
  perCamera: SvCameraResult[];
  totalMbPerSec: number;
  totalStorageTb: number;
  /** 'single' = 단일 컨트롤러(G) · 'dual' = 이중(R) · null = 이 제품군의 처리량을 넘음 */
  controller: 'single' | 'dual' | null;
  rows: SvRow[];
}

/** 카메라 1대 · 1fps 당 Mbps — 엑셀 Surveillance database B33 */
export function svBitratePerFps(c: Pick<SvCamera, 'stream' | 'resolution' | 'quality'>): number {
  if (c.stream === 'N/A') return 0;
  const qi = c.quality === 'Low' ? 0 : c.quality === 'Normal' ? 1 : 2;
  return SV_BITRATE[c.resolution][c.stream][qi];
}

/** 「CCTV 사이징」 — 엑셀 Surveillance 시트 + Surveillance database */
export function calcSurveillance(i: SvInput): SvResult {
  const perCamera: SvCameraResult[] = i.cameras.map((c) => {
    const per = svBitratePerFps(c);
    const mbps = per * c.fps * c.count;
    const mbPerSec = mbps / 8;
    // TB = Mbps × 3600초 × 시간 ÷ 8 ÷ 1024 × 일수 ÷ 1024 × 1.1 (엑셀 C25 그대로)
    const storageTb = ((mbps * 60 * 60 * c.hoursPerDay) / 8 / 1024) * c.days / 1024 * 1.1;
    const maxFps = c.stream === 'N/A' ? Infinity : SV_MAX_FPS[c.resolution];
    return { perCameraPerFps: per, mbps, mbPerSec, storageTb, overMaxFps: c.fps > maxFps };
  });
  const totalMbPerSec = perCamera.reduce((a, c) => a + c.mbPerSec, 0);
  const totalStorageTb = perCamera.reduce((a, c) => a + c.storageTb, 0);
  // ⚠ 엑셀은 352 미만 G · 352 초과 668 미만 R · 그 밖 「-」. 경계 352 는 단일 쪽으로.
  const controller: SvResult['controller'] =
    totalMbPerSec <= SV_CONTROLLER_LIMIT.single ? 'single' : totalMbPerSec < SV_CONTROLLER_LIMIT.dual ? 'dual' : null;
  const hostPrice = SV_HOST_BOARDS.find((h) => h.key === i.hostBoard)?.price ?? 0;
  const rows: SvRow[] = [];
  for (const tb of i.driveSizes) {
    for (const { system, jbod } of SV_CONFIGS) {
      rows.push(svRow(tb, system.name, jbod.name, controller, totalStorageTb, i, system, jbod, hostPrice));
    }
  }
  return { perCamera, totalMbPerSec, totalStorageTb, controller, rows };
}

function svRow(
  tb: number, sysName: string, jbName: string, controller: SvResult['controller'], totalTb: number, i: SvInput,
  sys: { bay: number; heightU: number; priceSingle: number; priceDual: number },
  jb: { bay: number; unit: number; drawers: boolean; heightU: number; priceSingle: number; priceDual: number; maxJbods: number; maxDrawers: number },
  hostPrice: number,
): SvRow {
  const suffix = controller === 'single' ? 'G' : controller === 'dual' ? 'R' : '';
  const system = controller === 'single' ? `${sysName}G` : controller === 'dual' ? `${sysName}RC` : sysName;
  const jbod = `${jbName}${suffix}`;
  const empty: SvRow = { driveTb: tb, system, jbod, config: null, totalDrives: null, usableTb: null, spares: null, heightU: null, msrp: null, msrpPerTb: null };
  if (controller === null) return empty;
  const mb = DRIVE_SIZE_MB[tb];
  if (mb === undefined) return empty;
  const s = i.spare;
  // 필요한 디스크 수(RAID 6 · 엑셀 G33) = 올림(TB × 1024² ÷ (MB − 256) ÷ (1 − 0.002))
  const need = roundUp((totalTb * 1024 * 1024) / (mb - DISK_RESERVED_MB) / (1 - VIRT_RATIO));
  const availSys = sys.bay - 2 - s;
  const jbodDrives = Math.max(need - availSys, 0);
  let units: number;      // 체인 JBOD 대수 또는 서랍 수
  let jbods: number;      // JBOD 대수 (가격·높이)
  let net: number;        // 데이터 디스크 수
  if (jb.drawers) {
    const availDrawer = jb.unit - 2 - s;
    const availJbod = 3 * availDrawer;
    units = roundUp(jbodDrives / availDrawer);
    jbods = roundUp(jbodDrives / availJbod);
    if (units > jb.maxDrawers || jbods > jb.maxJbods) return empty;
    net = availSys + availDrawer * units;
  } else {
    const availJbod = jb.bay - 2 - s;
    units = roundUp(jbodDrives / availJbod);
    jbods = units;
    if (jbods > jb.maxJbods) return empty;
    net = availSys + availJbod * units;
  }
  const totalDrives = sys.bay + jb.unit * units;
  const usableTb = net * usableTbPerDrive(tb);
  const spares = s * (1 + units);
  const heightU = sys.heightU + jb.heightU * jbods;
  const sysPrice = controller === 'single' ? sys.priceSingle : sys.priceDual;
  const jbPrice = controller === 'single' ? jb.priceSingle : jb.priceDual;
  const controllers = controller === 'single' ? 1 : 2;
  const msrp = sysPrice + jbPrice * jbods + hostPrice * controllers + (i.drivePrices[tb] ?? 0) * totalDrives;
  return {
    driveTb: tb, system, jbod, config: `1R+${jbods}J`, totalDrives, usableTb, spares, heightU, msrp,
    msrpPerTb: usableTb > 0 ? Math.round((msrp / usableTb) * 10) / 10 : null,
  };
}

/* ═══════════════════════ 5. VDI ═══════════════════════ */

export interface VdiInput {
  desktops: number;
  /** 데스크톱 1대당 평균 IOPS (정상 상태) */
  iopsPerDesktop: number;
  desktopGb: number;
  ramGb: number;
  clone: VdiClone;
  /** 복제본(replica)을 스토리지에 두나 */
  replicaInStorage: boolean;
  raid: VdiRaid;
  driveType: VdiDriveType;
  driveGb: number;
  desktopsPerLun: number;
}
export interface VdiResult {
  luns: number;
  lunsExact: number;
  steadyIops: number;
  bootStormIops: number;
  iopsPerDisk: number;
  disksForPerf: number;
  disksForBootStorm: number;
  /** 용량 계산 — LUN 1개 용량(GB) · 전체(GB) · 디스크 수(패리티 전) · RAID 몫 · 합 */
  lunCapacityGb: number;
  totalCapacityGb: number;
  disksForCapacityRaw: number;
  raidReduction: number;
  disksForCapacity: number;
  drivesRequired: number;
  drivesPerLun: number;
  configuredDrives: number;
  models: string;
  jbod24: number;
  usableLunGb: number;
  bootStormOk: boolean;
  /** 부트 스톰용 SSD 논리 드라이브 옵션 (RAID 1 / 5 / 6 디스크 수) · null = 지금 구성으로 충분 */
  ssdOptions: { raid1: number; raid5: number; raid6: number } | null;
}

/** 「VDI 사이징 · 유형 1」 — 엑셀 VDI 시트 + Calculate 시트 */
export function calcVdi(i: VdiInput): VdiResult {
  const lunsExact = i.desktops / i.desktopsPerLun;
  const luns = roundUp(lunsExact);
  const steadyIops = i.desktops * i.iopsPerDesktop;
  const bootStormIops = steadyIops * 5;
  const iopsPerDisk = VDI_IOPS_PER_DISK[i.driveType][i.raid];
  const disksForPerf = roundUp(steadyIops / iopsPerDisk);
  const disksForBootStorm = roundUp(bootStormIops / iopsPerDisk);
  // LUN 1개 용량 = 데스크톱 수 × (링크드 클론이면 용량 × 0.5 · 풀 클론이면 용량) + RAM) × 1.2 (20% 여유)
  const perDesktop = (i.clone === 'Linked' ? i.desktopGb * 0.5 : i.desktopGb) + i.ramGb;
  const lunCapacityGb = i.desktopsPerLun * perDesktop * 1.2;
  let totalCapacityGb: number;
  if (!i.replicaInStorage) {
    totalCapacityGb = lunCapacityGb * lunsExact;             // 엑셀 C20 은 올리지 않은 LUN 수를 곱한다
  } else {
    totalCapacityGb = (lunCapacityGb + i.desktopGb * 2) * luns; // 복제본 2벌 추가 · 엑셀 C26 은 올린 LUN 수
  }
  const disksForCapacityRaw = roundUp(totalCapacityGb / i.driveGb);
  const raidReduction = i.raid === 'RAID 5' ? luns : i.raid === 'RAID 6' ? luns * 2 : disksForCapacityRaw;
  const disksForCapacity = disksForCapacityRaw + raidReduction;
  const drivesRequired = Math.max(disksForPerf, disksForCapacity);
  let drivesPerLun = roundUp(drivesRequired / luns);
  if (i.raid === 'RAID 10' && drivesPerLun % 2 === 1) drivesPerLun += 1;
  const configuredDrives = luns * drivesPerLun;
  const models = vdiModels(bootStormIops, configuredDrives);
  const jbod24 = configuredDrives > 24 ? roundUp(configuredDrives / 24 - 1) : 0;
  const usableLunGb = i.raid === 'RAID 5' ? (drivesPerLun - 1) * i.driveGb : i.raid === 'RAID 6' ? (drivesPerLun - 2) * i.driveGb : (drivesPerLun / 2) * i.driveGb;
  const bootStormOk = i.driveType === 'SSD' && iopsPerDisk * configuredDrives > bootStormIops;
  let ssdOptions: VdiResult['ssdOptions'] = null;
  if (!bootStormOk) {
    let r1 = Math.max(roundUp(bootStormIops / VDI_BOOT_SSD.raid1.iops), VDI_BOOT_SSD.raid1.min);
    if (r1 % 2 === 1) r1 += 1;
    ssdOptions = {
      raid1: r1,
      raid5: Math.max(roundUp(bootStormIops / VDI_BOOT_SSD.raid5.iops), VDI_BOOT_SSD.raid5.min),
      raid6: Math.max(roundUp(bootStormIops / VDI_BOOT_SSD.raid6.iops), VDI_BOOT_SSD.raid6.min),
    };
  }
  return {
    luns, lunsExact, steadyIops, bootStormIops, iopsPerDisk, disksForPerf, disksForBootStorm,
    lunCapacityGb, totalCapacityGb, disksForCapacityRaw, raidReduction, disksForCapacity,
    drivesRequired, drivesPerLun, configuredDrives, models, jbod24, usableLunGb, bootStormOk, ssdOptions,
  };
}

/**
 * 권장 모델 — 부트 스톰 IOPS 와 디스크 수로 가른다 (엑셀 Calculate C34).
 *   ⚠ 엑셀의 47,000~75,000 · 361~444개 가지는 수식에 빈 인자가 끼어 늘 빈 글자다 — 옆 가지와 같은 「4000U」로 채웠다.
 */
export function vdiModels(bootStormIops: number, drives: number): string {
  if (drives > 444 || bootStormIops > 231000) return '맞는 모델 없음';
  const big = drives > 360;   // 361~444 는 4000U 만
  if (bootStormIops <= 47000) return big ? 'DS 4000U' : 'DS 1000T, 2000, 3000T, 4000U';
  if (bootStormIops <= 75000) return big ? 'DS 4000U' : 'DS 2000, 3000T, 4000U';
  if (bootStormIops <= 167000) return big ? 'DS 4000U' : 'DS 3000T, 4000U';
  return 'DS 4000U';
}

export interface VdiSupportInput { driveType: VdiDriveType; drives: number; raid: VdiRaid }
export interface VdiSupportResult {
  /** 디스크 1개 IOPS · null = 그 RAID 로 만들 수 없는 디스크 수 (RAID 10 홀수 · RAID 5 3개 미만 · RAID 6 4개 미만) */
  perDisk: number | null;
  totalIops: number;
  byModel: { model: string; light: number | null; knowledge: number | null; heavy: number | null }[];
}

/** 「VDI 사이징 · 유형 2」 — 구성한 디스크로 받을 수 있는 데스크톱 수 (용량은 안 본다) */
export function calcVdiSupport(i: VdiSupportInput): VdiSupportResult {
  const valid = i.raid === 'RAID 10' ? i.drives % 2 === 0 : i.raid === 'RAID 5' ? i.drives >= 3 : i.drives >= 4;
  const perDisk = valid ? VDI_IOPS_PER_DISK[i.driveType][i.raid] : null;
  const totalIops = (perDisk ?? 0) * i.drives;
  const w = (key: 'light' | 'knowledge' | 'heavy'): number => VDI_WORKERS.find((x) => x.key === key)!.iops;
  const byModel = VDI_MODELS.map((m) => {
    const isTop = m.maxDrives > 360;
    if (!isTop && i.drives > 360) return { model: m.name, light: null, knowledge: null, heavy: null };
    const users = (iopsPerUser: number): number =>
      (isTop ? i.drives <= m.maxDrives && totalIops <= m.maxIops : totalIops <= m.maxIops)
        ? roundUp(totalIops / iopsPerUser)
        : m.maxIops / iopsPerUser;
    return { model: m.name, light: users(w('light')), knowledge: users(w('knowledge')), heavy: users(w('heavy')) };
  });
  return { perDisk, totalIops, byModel };
}
